import styles from './styles.module.scss'
import FilterSidePanel from './FiltersSidePanel'
import { FilterOption } from 'types/filters'
import { FilterBuild } from './FiltersSidePanel/types'
import { useCallback, useEffect, useState } from 'react'
import { getCollectionFiltersState } from '../helpers'
import { getCustomerSavedFitFromCookies } from '@helpers/customer/fit'
import { getFiltersOptionsState } from '@helpers/filters'
import { ORDERED_OPTIONS } from '@consts/index'
import { initFit } from '@helpers/initFit'
import trackError from '@helpers/trackError'
import { updateFilterBuild } from '@helpers/collections/adapter/updateFilterBuild'

type NewCollectionFilterProps = {
  filterBuild: FilterBuild
  onApplyFitToggle: (state: boolean) => void
  onFilterClear: () => void
  onFilterApply: (args: FilterOption[]) => void
  filterOptions: FilterOption[]
}

function createFilterCookiesFromOptions(
  parsedCookie: FilterOption[],
  filterOptions: FilterOption[]
) {
  // Build from cookie, but skip empty values and options not present in filterOptions
  return Object.keys(parsedCookie).reduce<FilterOption[]>((arr, filterKey) => {
    const raw = parsedCookie[filterKey as unknown as keyof typeof parsedCookie]
    if (raw === undefined || raw === null) return arr

    const value = String(raw).trim()
    if (!value) return arr // <-- skip empty strings

    // Optional: only keep if it exists in available filterOptions
    const existsInOptions = filterOptions.some(
      fo => fo.type === filterKey && String(fo.value) === value
    )
    if (!existsInOptions) return arr

    return [
      ...arr,
      {
        type: filterKey,
        value,
        position:
          1 +
          ORDERED_OPTIONS.findIndex(
            option => filterKey === option.toLowerCase()
          )
      }
    ]
  }, [])
}

export default function CollectionFilter({
  filterOptions,
  filterBuild,
  onFilterClear,
  onApplyFitToggle,
  onFilterApply: handleFilterApply
}: NewCollectionFilterProps) {
  const [selectedFilterOptions, setSelectedFilterOptions] = useState<
    FilterOption[]
  >([])

  const filterBuildState = updateFilterBuild(filterBuild, selectedFilterOptions)

  useEffect(() => {
    // If Redux state is empty, also clear local state
    const isReduxFilterEmpty =
      Array.isArray(filterOptions) && filterOptions.length === 0
    if (isReduxFilterEmpty && selectedFilterOptions.length > 0) {
      setSelectedFilterOptions([])
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(filterOptions)])

  useEffect(() => {
    // If Redux state has values, and local state is empty, populate local state
    if (
      Array.isArray(filterOptions) &&
      filterOptions.length > 0 &&
      selectedFilterOptions.length === 0
    ) {
      setSelectedFilterOptions(filterOptions)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(filterOptions)])

  useEffect(() => {
    try {
      const parsedCookie = initFit()

      const filterOptionsFromCookie: FilterOption[] =
        createFilterCookiesFromOptions(
          parsedCookie as unknown as FilterOption[],
          filterOptions
        )

      if (filterOptionsFromCookie.length > 0) {
        setSelectedFilterOptions(filterOptionsFromCookie)
        handleFilterApply(filterOptionsFromCookie)
      }
    } catch (e) {
      trackError(e as Error)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleFilterSelect = () => {
    return (fitAttribute: string) => {
      return (selectedOption: string) => {
        setSelectedFilterOptions(prev =>
          getCollectionFiltersState(prev, fitAttribute, selectedOption)
        )
      }
    }
  }

  const handleFilterClear = useCallback(() => {
    setSelectedFilterOptions([])
    onFilterClear()
    onApplyFitToggle(false)
  }, [onFilterClear])

  const handleApplyFitToggle = useCallback(
    (state: boolean) => {
      onApplyFitToggle(state)

      if (!state) return setSelectedFilterOptions([])

      const savedFitFromCookies = getCustomerSavedFitFromCookies()
      setSelectedFilterOptions(
        savedFitFromCookies ? getFiltersOptionsState(savedFitFromCookies) : []
      )
    },
    [onApplyFitToggle]
  )

  const onHandleFilterApply = () => {
    handleFilterApply(selectedFilterOptions)
  }

  return (
    <div className={styles.filter__container}>
      <FilterSidePanel
        filters={filterBuildState}
        selectedFilterOptions={selectedFilterOptions}
        onFilterChange={handleFilterSelect}
        onFilterApply={onHandleFilterApply}
        onFilterClear={handleFilterClear}
        onApplyFitToggle={handleApplyFitToggle}
      />
    </div>
  )
}
